import React from "react";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { BeforeAfterSlider } from "./effects/BeforeAfterSlider";

const without = [
  "Limited visibility online",
  "Dependent on referrals & word of mouth",
  "Hard to build trust with new buyers",
  "Lost customers after business hours",
  "Difficult to compete with bigger brands",
];

const withWeb = [
  "Found on Google 24/7",
  "Steady stream of customer inquiries",
  "Professional, premium brand image",
  "Higher customer trust & conversions",
  "Strong competitive advantage in your city",
];

export const BeforeAfter = () => {
  return (
    <section
      id="before-after"
      data-testid="before-after-section"
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="max-w-2xl mb-16">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-4">
            Before vs After
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white">
            The difference a website <span className="gold-text italic">actually makes.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-testid="without-card"
            className="relative bg-[#0A0A0A] border border-[#222222] rounded-3xl p-8 sm:p-10"
          >
            <div className="text-[11px] tracking-[0.2em] uppercase text-[#737373] mb-3">
              Without a Website
            </div>
            <h3 className="font-display text-3xl text-white mb-8">You stay invisible.</h3>
            <ul className="space-y-4">
              {without.map((w) => (
                <li
                  key={w}
                  className="flex items-start gap-3 text-[#B0B0B0]"
                  data-testid="without-item"
                >
                  <span className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 mt-0.5">
                    <X className="w-3.5 h-3.5 text-[#737373]" />
                  </span>
                  <span className="leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-testid="with-card"
            className="relative rounded-3xl p-8 sm:p-10 overflow-hidden"
            style={{
              background:
                "linear-gradient(180deg, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0.02) 100%)",
              border: "1px solid rgba(212,175,55,0.35)",
            }}
          >
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-[#D4AF37]/20 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-3">
                With a Website by Divy
              </div>
              <h3 className="font-display text-3xl text-white mb-8">
                You become the obvious choice.
              </h3>
              <ul className="space-y-4">
                {withWeb.map((w) => (
                  <li
                    key={w}
                    className="flex items-start gap-3 text-white"
                    data-testid="with-item"
                  >
                    <span className="w-6 h-6 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-[#D4AF37]" />
                    </span>
                    <span className="leading-relaxed">{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Visual transformation slider */}
        <div className="mt-12">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-4">
            Drag to see the transformation
          </div>
          <BeforeAfterSlider
            before="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1600&q=70"
            after="https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1600&q=70"
            beforeLabel="Old / no website"
            afterLabel="Premium build"
          />
        </div>
      </div>
    </section>
  );
};
